import { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../../../main/theme/AppThemeContext';
import '../../../main/theme/theme.css';
import './AuthSplitLayout.css';

type AuthSplitLayoutProps = {
  mode: 'login' | 'register';
  children: ReactNode;
  footer?: ReactNode;
};

const AuthSplitLayout = ({ mode, children, footer }: AuthSplitLayoutProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useAppTheme();
  const isRegister = mode === 'register';

  return (
    <div className={`authSplitPage authPortal bcShell bcTheme-${theme} authSplitPage--${mode}`}>
      <motion.div
        className="authPortalShell"
        initial={{ opacity: 0, y: 18, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="authPortalGlow authPortalGlow--one" />
        <div className="authPortalGlow authPortalGlow--two" />

        <AnimatePresence initial={false}>
          <motion.section
            key={`form-${mode}`}
            className="authPortalForm"
            initial={{ opacity: 0, x: isRegister ? 34 : -34 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRegister ? -24 : 24 }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <div className="authPortalFormInner">{children}</div>
            {footer ? <div className="authPortalFooter">{footer}</div> : null}
          </motion.section>
        </AnimatePresence>

        <motion.aside
          className="authPortalWelcome"
          layout
          transition={{ type: 'spring', stiffness: 120, damping: 22 }}
        >
          <div className="authPortalBrand">
            <span className="authPortalBrandMark">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2 3 7l9 5 9-5-9-5Zm-9 10 9 5 9-5M3 17l9 5 9-5" />
              </svg>
            </span>
            <span>TechPOS</span>
          </div>

          <AnimatePresence>
            <motion.div
              key={`welcome-${mode}`}
              className="authPortalWelcomeCopy"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, delay: 0.08 }}
            >
              <span className="authPortalKicker">
                {t(isRegister ? 'login.authRegisterKicker' : 'login.authLoginKicker')}
              </span>
              <h1>{t(isRegister ? 'login.authRegisterHeroTitle' : 'login.authLoginHeroTitle')}</h1>
              <p>{t('login.authHeroText')}</p>

              <button
                type="button"
                className="authPortalSwitch"
                onClick={() => navigate(isRegister ? '/login' : '/login?register=true')}
              >
                {t(isRegister ? 'login.authSwitchToLogin' : 'login.authSwitchToRegister')}
                <span aria-hidden="true">→</span>
              </button>
            </motion.div>
          </AnimatePresence>

          <div className="authPortalWelcomeFooter">
            <span>{t('login.privacyPolicy')}</span>
            <i />
            <span>{t('login.termsOfService')}</span>
          </div>
        </motion.aside>
      </motion.div>
    </div>
  );
};

export default AuthSplitLayout;
